import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import {
  DEFAULT_META_DESCRIPTION,
  DEFAULT_PAGE_TITLE,
  INDEXABLE_ROBOTS_CONTENT,
  NOINDEX_ROBOTS_CONTENT,
  SITE_NAME,
  hasTransientNoindexQuery,
  toAbsoluteUrl,
} from './seo-constants'

interface SeoHeadProps {
  title?: string
  description?: string
  canonicalPath?: string
  canonicalUrl?: string
  imageUrl?: string
  robots?: string
  noindex?: boolean
  ogType?: 'website' | 'article' | 'video.other'
}

function upsertMetaTag(
  attributeName: 'name' | 'property',
  attributeValue: string,
  content: string,
): void {
  const selector = `meta[${attributeName}="${attributeValue}"]`
  let meta = document.head.querySelector<HTMLMetaElement>(selector)

  if (!content) {
    if (meta) {
      meta.remove()
    }
    return
  }

  if (!meta) {
    meta = document.createElement('meta')
    meta.setAttribute(attributeName, attributeValue)
    document.head.appendChild(meta)
  }

  meta.setAttribute('content', content)
}

function upsertCanonicalLink(href: string): void {
  let canonical = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]')
  if (!canonical) {
    canonical = document.createElement('link')
    canonical.setAttribute('rel', 'canonical')
    document.head.appendChild(canonical)
  }
  canonical.setAttribute('href', href)
}

export default function SeoHead({
  title,
  description,
  canonicalPath,
  canonicalUrl,
  imageUrl,
  robots,
  noindex = false,
  ogType = 'website',
}: SeoHeadProps) {
  const location = useLocation()

  useEffect(() => {
    const effectiveTitle = title?.trim() || DEFAULT_PAGE_TITLE
    const effectiveDescription = description?.trim() || DEFAULT_META_DESCRIPTION
    const canonicalTarget = canonicalUrl
      ? toAbsoluteUrl(canonicalUrl)
      : toAbsoluteUrl(canonicalPath || location.pathname)
    const effectiveNoindex = noindex || hasTransientNoindexQuery(location.search)
    const robotsContent =
      robots || (effectiveNoindex ? NOINDEX_ROBOTS_CONTENT : INDEXABLE_ROBOTS_CONTENT)
    const effectiveImageUrl = imageUrl ? toAbsoluteUrl(imageUrl) : ''
    const twitterCard = effectiveImageUrl ? 'summary_large_image' : 'summary'

    document.title = effectiveTitle
    upsertCanonicalLink(canonicalTarget)
    upsertMetaTag('name', 'description', effectiveDescription)
    upsertMetaTag('name', 'robots', robotsContent)

    upsertMetaTag('property', 'og:site_name', SITE_NAME)
    upsertMetaTag('property', 'og:type', ogType)
    upsertMetaTag('property', 'og:title', effectiveTitle)
    upsertMetaTag('property', 'og:description', effectiveDescription)
    upsertMetaTag('property', 'og:url', canonicalTarget)
    upsertMetaTag('property', 'og:image', effectiveImageUrl)

    upsertMetaTag('name', 'twitter:card', twitterCard)
    upsertMetaTag('name', 'twitter:title', effectiveTitle)
    upsertMetaTag('name', 'twitter:description', effectiveDescription)
    upsertMetaTag('name', 'twitter:image', effectiveImageUrl)
  }, [
    canonicalPath,
    canonicalUrl,
    description,
    imageUrl,
    location.pathname,
    location.search,
    noindex,
    ogType,
    robots,
    title,
  ])

  return null
}
